import type { ReactNode } from "react";
import { cx } from "@frontend/components/ui";

/**
 * Set these when the listings go live — badges become real links automatically.
 * Leave null to keep the “Coming soon” state.
 */
export const STORE_LINKS = {
  appStore: null as string | null,
  playStore: "https://play.google.com/store/apps/details?id=me.sendcollab.app",
} as const;

type Tone = "light" | "dark";

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M16.7 12.6c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3.1-1.6-1.3-.1-2.6.8-3.2.8s-1.7-.8-2.8-.7c-1.4.1-2.8.9-3.5 2.2-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.8 2.1 1.1-.1 1.5-.7 2.9-.7s1.7.7 2.9.7c1.2 0 2-.1 2.8-1.1.9-1.1 1.2-2.1 1.2-2.2-.1 0-2.3-.9-2.3-3.5zM14.8 6.3c.6-.8 1.1-1.8.9-2.9-.9.1-2 .6-2.7 1.4-.6.7-1.1 1.8-.9 2.8 1 .1 2-.5 2.7-1.3z" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  // Google Play mark — distinctive four-panel triangle.
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M3 20.5V3.5c0-.59.34-1.11.84-1.35L13.69 12 3.84 21.85C3.34 21.6 3 21.09 3 20.5m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.59.69.59 1.19s-.22.9-.57 1.18l-2.29 1.32-2.5-2.5 2.5-2.5 2.27 1.31M6.05 2.66l10.76 6.22-2.27 2.27L6.05 2.66z" />
    </svg>
  );
}

function StoreBadge({
  href,
  eyebrow,
  title,
  icon,
  tone,
  compact,
}: {
  href: string | null;
  eyebrow: string;
  title: string;
  icon: ReactNode;
  tone: Tone;
  compact?: boolean;
}) {
  const comingSoon = !href;
  const className = cx(
    "inline-flex items-center text-left transition",
    "focus-visible:outline-none focus-visible:ring-4",
    compact
      ? "min-w-[9.5rem] gap-2 rounded-lg px-3 py-2"
      : "min-w-[10.5rem] gap-2.5 rounded-xl px-3.5 py-2.5",
    tone === "dark"
      ? "border border-canvas/20 bg-canvas/10 text-canvas focus-visible:ring-canvas/25"
      : "border border-line bg-surface text-ink shadow-sm shadow-ink/5 focus-visible:ring-brand-500/25",
    comingSoon
      ? "cursor-default opacity-85"
      : tone === "dark"
        ? "hover:bg-canvas/15"
        : "hover:bg-ink/[0.03]",
  );

  const body = (
    <>
      <span className="shrink-0">{icon}</span>
      <span className="min-w-0 leading-none">
        <span
          className={cx(
            "block font-medium tracking-wide uppercase",
            compact ? "text-[9px]" : "text-[10px]",
            tone === "dark" ? "text-canvas/65" : "text-ink-subtle",
          )}
        >
          {comingSoon ? "Coming soon" : eyebrow}
        </span>
        <span
          className={cx(
            "mt-0.5 block font-semibold tracking-tight",
            compact ? "text-[13px]" : "mt-1 text-sm",
          )}
        >
          {title}
        </span>
      </span>
    </>
  );

  if (comingSoon) {
    return (
      <span className={className} aria-disabled="true" title="Coming soon">
        {body}
      </span>
    );
  }

  return (
    <a href={href} className={className} target="_blank" rel="noopener noreferrer">
      {body}
    </a>
  );
}

export function StoreBadges({
  tone = "light",
  compact = false,
  className,
}: {
  tone?: Tone;
  compact?: boolean;
  className?: string;
}) {
  const iconClass = compact ? "size-5" : "size-7";
  return (
    <div
      className={cx(
        "flex flex-col sm:flex-row sm:flex-wrap",
        compact ? "gap-2" : "gap-2.5",
        className,
      )}
    >
      <StoreBadge
        href={STORE_LINKS.appStore}
        eyebrow="Download on the"
        title="App Store"
        tone={tone}
        compact={compact}
        icon={<AppleIcon className={iconClass} />}
      />
      <StoreBadge
        href={STORE_LINKS.playStore}
        eyebrow="Get it on"
        title="Google Play"
        tone={tone}
        compact={compact}
        icon={<PlayIcon className={iconClass} />}
      />
    </div>
  );
}
